import { useCallback } from 'react';
import type { DataGroup } from '@/features/DataGrid';
import { useMetricsStabilizedValue } from '@/shared/hooks';
import { useAppDispatch } from '@/shared/model';
import { getPage, getTotalData, getTreeData, getMetricsMinMax } from './slice';
import type { DataConfig } from './types';

export function useDataFetchers(config: DataConfig) {
  const dispatch = useAppDispatch();
  const stableMetrics = useMetricsStabilizedValue(config.metrics);

  const onGetRows = useCallback(
    (offset?: number) => {
      return dispatch(
        getPage({
          dimensions: config.dimensions,
          metrics: stableMetrics,
          cohortDateScale: config.cohortDateScale,
          cohortDateRange: config.cohortDateRange,
          revenueDateScale: config.revenueDateScale,
          revenueDateRange: config.revenueDateRange,
          sorting: config.sorting,
          probabilisticAttribution: config.probabilisticAttribution,
          filters: config.filters,
          offset,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      stableMetrics,
      config.cohortDateScale,
      config.cohortDateRange,
      config.revenueDateScale,
      config.revenueDateRange,
      config.sorting,
      config.probabilisticAttribution,
      config.filters,
    ],
  );

  const onGetTotal = useCallback(
    () => {
      return dispatch(
        getTotalData({
          dimensions: config.dimensions,
          metrics: stableMetrics,
          cohortDateRange: config.cohortDateRange,
          revenueDateRange: config.revenueDateRange,
          probabilisticAttribution: config.probabilisticAttribution,
          filters: config.filters,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      stableMetrics,
      config.cohortDateRange,
      config.revenueDateRange,
      config.probabilisticAttribution,
      config.filters,
    ],
  );

  const onGetTreeData = useCallback(
    (dataGroups: DataGroup[]) => {
      return dispatch(
        getTreeData({
          dimensions: config.dimensions,
          metrics: stableMetrics,
          cohortDateScale: config.cohortDateScale,
          cohortDateRange: config.cohortDateRange,
          revenueDateScale: config.revenueDateScale,
          revenueDateRange: config.revenueDateRange,
          sorting: config.sorting,
          probabilisticAttribution: config.probabilisticAttribution,
          filters: config.filters,
          parentGroups: dataGroups,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      stableMetrics,
      config.cohortDateScale,
      config.cohortDateRange,
      config.revenueDateScale,
      config.revenueDateRange,
      config.sorting,
      config.probabilisticAttribution,
      config.filters,
    ],
  );

  const onGetMinMax = useCallback(
    () => {
      return dispatch(
        getMetricsMinMax({
          dimensions: config.dimensions,
          metrics: stableMetrics,
          filters: config.filters,
          cohortDateRange: config.cohortDateRange,
          cohortDateScale: config.cohortDateScale,
          revenueDateRange: config.revenueDateRange,
          revenueDateScale: config.revenueDateScale,
          probabilisticAttribution: config.probabilisticAttribution,
          dataGridViewType: config.dataGridViewType,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dataGridViewType,
      config.dimensions,
      stableMetrics,
      config.filters,
      config.cohortDateRange,
      config.cohortDateScale,
      config.revenueDateRange,
      config.revenueDateScale,
      config.probabilisticAttribution,
    ],
  );

  return { onGetRows, onGetTotal, onGetTreeData, onGetMinMax };
}
